import React, {useState} from "react";
import {ArrowDownIcon, ArrowUpIcon, PlusIcon, SaveIcon, Trash2Icon} from "lucide-react";
import Button from "../../../../../components/Button";
import {MarkdownField} from "../../../../../components/MarkdownField";
import {isoToWindhoekInput, roleLabel, SLOT_KINDS, SPEAKER_ROLES} from "../../../../../lib/eventProgramme";
import type {EventDayResponse, EventLineupSlot, EventSpeaker, LineupSlotKind, SpeakerRole} from "../../../../../types/event";

type SlotSpeakerDraft = { speakerId: string; role: SpeakerRole };

export type SlotDraft = {
    eventDayId: string;
    startsAt: string; // datetime-local value, Windhoek wall time
    endsAt: string;
    title: string;
    description: string;
    kind: LineupSlotKind;
    stage: string;
    speakers: SlotSpeakerDraft[];
};

/** The slot body for POST or PUT. Times go without an offset, which the backend reads as Windhoek. */
export function slotRequest(d: SlotDraft) {
    const blank = (s: string) => s.trim() || null;
    return {
        eventDayId: d.eventDayId || null,
        startsAt: d.startsAt,
        endsAt: d.endsAt || null,
        title: d.title.trim(),
        description: blank(d.description),
        kind: d.kind,
        stage: blank(d.stage),
        speakers: d.speakers,
    };
}

const inputCls =
    "mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-orange-600 focus:outline-none";

function move<T>(arr: T[], from: number, to: number): T[] {
    if (to < 0 || to >= arr.length) return arr;
    const copy = [...arr];
    const [x] = copy.splice(from, 1);
    copy.splice(to, 0, x);
    return copy;
}

/**
 * Add or edit one line-up slot. Speakers come from the event's own list, each with the role they
 * play in this slot, prefilled from their first role.
 */
export default function LineupSlotEditor({
                                             slot,
                                             days,
                                             speakers,
                                             saving,
                                             onSave,
                                             onCancel,
                                         }: {
    slot: EventLineupSlot | null;
    days: EventDayResponse[];
    speakers: EventSpeaker[];
    saving: boolean;
    onSave: (draft: SlotDraft) => void;
    onCancel: () => void;
}) {
    const [draft, setDraft] = useState<SlotDraft>(() => ({
        eventDayId: slot?.eventDayId ?? "",
        startsAt: isoToWindhoekInput(slot?.startsAt),
        endsAt: isoToWindhoekInput(slot?.endsAt),
        title: slot?.title ?? "",
        description: slot?.description ?? "",
        kind: slot?.kind ?? "TALK",
        stage: slot?.stage ?? "",
        speakers: (slot?.speakers ?? []).map((s) => ({speakerId: s.speakerId, role: s.role})),
    }));
    const [pickId, setPickId] = useState("");

    const set = (patch: Partial<SlotDraft>) => setDraft((d) => ({...d, ...patch}));
    const byId = (id: string) => speakers.find((s) => s.id === id);
    const available = speakers.filter((s) => !draft.speakers.some((x) => x.speakerId === s.id));

    const addSpeaker = () => {
        const s = byId(pickId);
        if (!s) return;
        set({speakers: [...draft.speakers, {speakerId: s.id, role: s.roles?.[0] ?? "SPEAKER"}]});
        setPickId("");
    };

    const endBeforeStart = !!draft.startsAt && !!draft.endsAt && draft.endsAt <= draft.startsAt;
    const canSave = draft.title.trim().length > 0 && !!draft.startsAt && !endBeforeStart && !saving;

    return (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900">{slot ? "Edit slot" : "New slot"}</h2>
                    <p className="mt-1 text-sm text-gray-600">Times are local Windhoek time.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button type="button" variant="secondary" size="md" onClick={onCancel} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        size="md"
                        onClick={() => onSave(draft)}
                        disabled={!canSave}
                        leftIcon={saving ? undefined : <SaveIcon className="size-4" aria-hidden="true"/>}
                    >
                        {saving ? "Saving…" : "Save slot"}
                    </Button>
                </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                    <label htmlFor="slot-title" className="block text-sm font-medium text-gray-900">Title *</label>
                    <input id="slot-title" value={draft.title} maxLength={200}
                           onChange={(e) => set({title: e.target.value})} className={inputCls}
                           placeholder="e.g. Opening keynote"/>
                </div>

                <div>
                    <label htmlFor="slot-day" className="block text-sm font-medium text-gray-900">Event day</label>
                    <select id="slot-day" value={draft.eventDayId} onChange={(e) => set({eventDayId: e.target.value})}
                            className={inputCls}>
                        <option value="">No specific day</option>
                        {days.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                </div>

                <div>
                    <label htmlFor="slot-kind" className="block text-sm font-medium text-gray-900">Kind</label>
                    <select id="slot-kind" value={draft.kind} onChange={(e) => set({kind: e.target.value})}
                            className={inputCls}>
                        {SLOT_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                    </select>
                </div>

                <div>
                    <label htmlFor="slot-starts" className="block text-sm font-medium text-gray-900">Starts *</label>
                    <input id="slot-starts" type="datetime-local" value={draft.startsAt}
                           onChange={(e) => set({startsAt: e.target.value})} className={inputCls}/>
                </div>

                <div>
                    <label htmlFor="slot-ends" className="block text-sm font-medium text-gray-900">Ends</label>
                    <input id="slot-ends" type="datetime-local" value={draft.endsAt}
                           aria-invalid={endBeforeStart} aria-describedby={endBeforeStart ? "slot-ends-error" : undefined}
                           onChange={(e) => set({endsAt: e.target.value})} className={inputCls}/>
                    {endBeforeStart ? (
                        <p id="slot-ends-error" className="mt-1 text-xs text-red-700">The end must be after the start.</p>
                    ) : null}
                </div>

                <div className="sm:col-span-2">
                    <label htmlFor="slot-stage" className="block text-sm font-medium text-gray-900">Stage</label>
                    <input id="slot-stage" value={draft.stage} maxLength={120}
                           onChange={(e) => set({stage: e.target.value})} className={inputCls}
                           placeholder="e.g. Main stage"/>
                </div>
            </div>

            <fieldset className="mt-6">
                <legend className="block text-sm font-medium text-gray-900">Speakers</legend>
                {speakers.length === 0 ? (
                    <p className="mt-1 text-xs text-gray-500">Add speakers to the event first to pick them here.</p>
                ) : (
                    <>
                        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                            <label htmlFor="slot-pick-speaker" className="sr-only">Speaker to add</label>
                            <select id="slot-pick-speaker" value={pickId} onChange={(e) => setPickId(e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-orange-600 focus:outline-none">
                                <option value="">Choose a speaker…</option>
                                {available.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                            <Button type="button" variant="secondary" size="md" onClick={addSpeaker} disabled={!pickId}
                                    leftIcon={<PlusIcon className="size-4" aria-hidden="true"/>}>
                                Add
                            </Button>
                        </div>

                        {draft.speakers.length ? (
                            <ul className="mt-3 divide-y divide-gray-100 rounded-xl border border-gray-200">
                                {draft.speakers.map((row, idx) => {
                                    const s = byId(row.speakerId);
                                    const name = s?.name ?? slot?.speakers.find((x) => x.speakerId === row.speakerId)?.name ?? "Speaker";
                                    // the speaker's own roles first, then the rest: the usual choice sits near the top
                                    const own = s?.roles ?? [];
                                    const options = [...own, ...SPEAKER_ROLES.map((r) => r.value).filter((r) => !own.includes(r))];
                                    if (!options.includes(row.role)) options.push(row.role);
                                    const update = (patch: Partial<SlotSpeakerDraft>) =>
                                        set({speakers: draft.speakers.map((x, i) => (i === idx ? {...x, ...patch} : x))});
                                    return (
                                        <li key={row.speakerId} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center">
                                            <div className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">{name}</div>
                                            <label htmlFor={`slot-role-${row.speakerId}`} className="sr-only">
                                                Role of {name} in this slot
                                            </label>
                                            <select id={`slot-role-${row.speakerId}`} value={row.role}
                                                    onChange={(e) => update({role: e.target.value})}
                                                    className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-orange-600 focus:outline-none">
                                                {options.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
                                            </select>
                                            <div className="flex items-center gap-2">
                                                <button type="button" aria-label={`Move ${name} up`} disabled={idx === 0}
                                                        onClick={() => set({speakers: move(draft.speakers, idx, idx - 1)})}
                                                        className="rounded-lg border border-gray-200 bg-white p-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                                                    <ArrowUpIcon className="size-4"/>
                                                </button>
                                                <button type="button" aria-label={`Move ${name} down`}
                                                        disabled={idx === draft.speakers.length - 1}
                                                        onClick={() => set({speakers: move(draft.speakers, idx, idx + 1)})}
                                                        className="rounded-lg border border-gray-200 bg-white p-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                                                    <ArrowDownIcon className="size-4"/>
                                                </button>
                                                <button type="button" aria-label={`Remove ${name} from this slot`}
                                                        onClick={() => set({speakers: draft.speakers.filter((_, i) => i !== idx)})}
                                                        className="rounded-lg border border-red-200 bg-white p-2 text-red-700 hover:bg-red-50">
                                                    <Trash2Icon className="size-4"/>
                                                </button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <p className="mt-2 text-xs text-gray-500">No speakers in this slot. Breaks usually have none.</p>
                        )}
                    </>
                )}
            </fieldset>

            <div className="mt-6">
                <MarkdownField
                    title="Description"
                    description="Optional. Markdown is supported."
                    value={draft.description}
                    onChange={(v) => set({description: v.slice(0, 10000)})}
                />
            </div>
        </div>
    );
}
